// lib/seedvr2.ts

export const SEEDVR2_OUTPUT_PIXELS = {
  4: 4_000_000,
  8: 8_000_000,
  12: 12_000_000,
  16: 16_000_000,
  20: 20_000_000,
  24: 24_000_000,
  28: 28_000_000,
  30: 30_000_000,
  32: 32_000_000,
  36: 36_000_000,
  40: 40_000_000,
} as const;

export const SEEDVR2_INPUT_PIXELS = {
  0.5: 500_000,
  1: 1_000_000,
  2: 2_000_000,
  3: 3_000_000,
  4: 4_000_000,
  5: 5_000_000,
  10: 10_000_000,
  16: 16_000_000,
} as const;

// 30 Mpx stays in the pixel map because topaz-image-enhance still offers it; SeedVR2 does not.
export const SEEDVR2_OUTPUT_MEGAPIXEL_OPTIONS = [4, 8, 12, 16, 20, 24, 28, 32, 36, 40] as const;
export const SEEDVR2_INPUT_MEGAPIXEL_OPTIONS = [0.5, 1, 2, 3, 4, 5, 10, 16] as const;

export type SeedVR2OutputMegaPixels = typeof SEEDVR2_OUTPUT_MEGAPIXEL_OPTIONS[number];
export type SeedVR2InputMegaPixels = keyof typeof SEEDVR2_INPUT_PIXELS;

export const SEEDVR2_UNET_FILENAME = 'seedvr2_7b_fp8_e4m3fn.safetensors';
export const SEEDVR2_MAX_UPSCALE_FACTOR = 8;

export const SEEDVR2_STANDARD_POSTPROCESS = {
  harmonize: 'On',
  harmonize_profile: 'strict',
  // Masking out higher-resolution layers leaves a SeedVR2 output (grid tiles above all) partly
  // transparent; an upscale rarely sits over sharper content, so the defect is not worth the rare gain.
  preserve_high_res: false,
  alpha_radius: 64,
} as const;

export const normalizeSeedVR2InputMegaPixels = (value: unknown): SeedVR2InputMegaPixels | 'auto' => {
  if (value === 'auto') return value;
  return (SEEDVR2_INPUT_MEGAPIXEL_OPTIONS as readonly number[]).includes(value as number)
    ? value as SeedVR2InputMegaPixels
    : 2;
};

export const isSeedVR2OutputMegaPixels = (value: unknown): value is SeedVR2OutputMegaPixels => (
  (SEEDVR2_OUTPUT_MEGAPIXEL_OPTIONS as readonly number[]).includes(value as number)
);

export const normalizeSeedVR2OutputMegaPixels = (value: unknown): SeedVR2OutputMegaPixels => (
  isSeedVR2OutputMegaPixels(value) ? value : 32
);

export const resolveSeedVR2EffectiveInputMegaPixels = (inputValue: unknown, outputValue: unknown): number => {
  const input = normalizeSeedVR2InputMegaPixels(inputValue);
  const requestedMegaPixels = input === 'auto' ? 16 : input;
  return Math.min(requestedMegaPixels, normalizeSeedVR2OutputMegaPixels(outputValue) / 64);
};

export const resolveSeedVR2InputPixelBudget = (inputValue: unknown, outputValue: unknown = 32): number => (
  resolveSeedVR2EffectiveInputMegaPixels(inputValue, outputValue) * 1_000_000
);

export const resolveSeedVR2InputRenderPixelBudget = ({
  inputValue,
  outputValue,
}: {
  inputValue: unknown;
  outputValue: unknown;
}): number => resolveSeedVR2InputPixelBudget(inputValue, outputValue);

export const calculateSeedVR2UpscaleFactor = ({
  sourceWidth,
  sourceHeight,
  targetPixels,
}: {
  sourceWidth: number;
  sourceHeight: number;
  targetPixels: number;
}): number => {
  if (!Number.isFinite(sourceWidth) || sourceWidth <= 0) return 1;
  if (!Number.isFinite(sourceHeight) || sourceHeight <= 0) return 1;
  if (!Number.isFinite(targetPixels) || targetPixels <= 0) return 1;
  return Math.max(1, Math.sqrt(targetPixels / (sourceWidth * sourceHeight)));
};

export const resolveSeedVR2OutputDimensions = ({
  sourceWidth,
  sourceHeight,
  targetPixels,
}: {
  sourceWidth: number;
  sourceHeight: number;
  targetPixels: number;
}): { width: number; height: number; upscaleFactor: number } => {
  const upscaleFactor = calculateSeedVR2UpscaleFactor({ sourceWidth, sourceHeight, targetPixels });
  return {
    width: Math.max(1, Math.round(sourceWidth * upscaleFactor)),
    height: Math.max(1, Math.round(sourceHeight * upscaleFactor)),
    upscaleFactor,
  };
};

export const resolveSeedVR2InputDimensions = ({
  sourceWidth,
  sourceHeight,
  targetPixels,
}: {
  sourceWidth: number;
  sourceHeight: number;
  targetPixels: number;
}): { width: number; height: number; scaleFactor: number } => {
  if (!Number.isFinite(sourceWidth) || sourceWidth <= 0 || !Number.isFinite(sourceHeight) || sourceHeight <= 0) {
    return { width: 1, height: 1, scaleFactor: 1 };
  }
  const scaleFactor = Number.isFinite(targetPixels) && targetPixels > 0
    ? Math.sqrt(targetPixels / (sourceWidth * sourceHeight))
    : 1;
  let width = Math.max(1, Math.floor(sourceWidth * scaleFactor));
  let height = Math.max(1, Math.floor(sourceHeight * scaleFactor));
  while (width * height > targetPixels && width > 1 && height > 1) {
    if (width / sourceWidth >= height / sourceHeight) width -= 1;
    else height -= 1;
  }
  return { width, height, scaleFactor };
};

// The /seamless variant wraps the edges for tileable textures; Studio crops are not tileable.
export const SEEDVR2_FAL_ENDPOINT = 'fal-ai/seedvr/upscale/image';

export const buildSeedVR2FalInput = ({
  imageUrl,
  seed,
  upscaleFactor,
}: {
  imageUrl: string;
  seed: unknown;
  upscaleFactor: number;
}): Record<string, unknown> => ({
  image_url: imageUrl,
  upscale_mode: 'factor',
  upscale_factor: Math.min(SEEDVR2_MAX_UPSCALE_FACTOR, upscaleFactor),
  output_format: 'webp',
  ...(typeof seed === 'number' && Number.isInteger(seed) && seed >= 0 ? { seed } : {}),
});
